/**
 * URL de callback do Auth0 para o ambiente atual.
 *
 * Após tentativa de login (sucesso ou erro), o Auth0 retorna para `/private`
 * — nunca para a home pública. Em deploy previews isso vira, por exemplo,
 * `https://deploy-preview-N--mette.netlify.app/private`.
 *
 * Essas URLs precisam estar cadastradas no painel do Auth0
 * (Allowed Callback URLs) — ver src/pages/Private/Documentation/overview.mdx.
 */
export function getAuth0RedirectUri() {
  if (process.env.REACT_APP_AUTH0_REDIRECT_URI) {
    return process.env.REACT_APP_AUTH0_REDIRECT_URI;
  }

  return `${window.location.origin}/private`;
}

export const AUTH0_APP_STATE = { returnTo: '/private' };

/**
 * True quando o usuário fechou o popup do Auth0 sem concluir o login.
 * O SDK lança PopupCancelledError: error === 'cancelled', message === 'Popup closed'.
 */
export function isAuth0PopupClosed(error) {
  if (!error) return false;

  if (error.error === 'cancelled') return true;

  const raw = (
    error.message ||
    error.error_description ||
    (typeof error === 'string' ? error : '')
  ).toLowerCase();

  return (
    raw.includes('popup closed') ||
    raw.includes('popup_closed') ||
    raw === 'cancelled'
  );
}

/**
 * Mensagem de erro legível a partir do Auth0 ou da query string.
 * Retorna `null` se não houver mensagem a exibir (ex.: string vazia).
 */
export function formatAuth0Error(errorOrDescription) {
  if (!errorOrDescription) return null;

  if (isAuth0PopupClosed(errorOrDescription)) {
    return 'Login cancelado: a janela foi fechada sem autenticação.';
  }

  const raw =
    typeof errorOrDescription === 'string'
      ? errorOrDescription
      : errorOrDescription.message ||
        errorOrDescription.error_description ||
        errorOrDescription.error ||
        String(errorOrDescription);

  const normalized = raw.toLowerCase();

  if (normalized.includes('user is blocked') || normalized.includes('blocked')) {
    return 'Usuário bloqueado. Entre em contato com o administrador.';
  }

  if (
    normalized.includes('access_denied') ||
    normalized.includes('unauthorized') ||
    normalized.includes('not allowed')
  ) {
    return 'Sem permissão para acessar a área privada.';
  }

  if (
    normalized.includes('popup_open') ||
    normalized.includes('unable to open a popup')
  ) {
    return 'Não foi possível abrir a janela de login. Verifique se o navegador bloqueou popups.';
  }

  return raw;
}
